import React from 'react';
import bem from '../../../lib/bemUtils/bem';
import { ButtonRound } from '../../../components/molecules/Btn/ButtonWrapper';
import { GlobalDocumentIds } from '../../../types/GlobalDocumentIds';
import { murmurhash } from '../../../lib/js/hash';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import ILink from '../../../types/ILink';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { isNullOrUndefined } from '../../../lib/typeGuards';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { Icons } from '../../../components/atoms/SVG';

export enum PaginationModifier {
  BORDER_TOP = 'border-top',
}

export interface IFetchLink extends ILink {
  fetchUrl: string;
}

type PageItem<TType extends string, TLinkKey extends string, TLink> = {
  type: TType;
  active: boolean;
  current: boolean;
} & { [P in TLinkKey]: TLink };

type PageLinkItem = PageItem<'previous' | 'page' | 'next', 'pageLink', ILink>;
type PageLinkSelector = PageItem<'pageSelector', 'pageLink', null | undefined>;

export type PageFetchLinkItem = PageItem<'previous' | 'page' | 'next', 'fetchLink', IFetchLink>;
type PageFetchLinkSelector = PageItem<'pageSelector', 'fetchLink', null | undefined>;

export type PaginationItemWithPageLink = PageLinkItem | PageLinkSelector;
export type PaginationItemWithFetchLink = PageFetchLinkItem | PageFetchLinkSelector;

export type PaginationItem = PaginationItemWithPageLink | PaginationItemWithFetchLink;

type OnClickArgs<T> = Exclude<T, PageLinkSelector | PageFetchLinkSelector>;
type Pagination<T> = {
  pageItems?: PaginationItem[];
  modifier?: Modifier<PaginationModifier>;
  parentHash?: string;
  onClick?: (item: OnClickArgs<T>) => void;
};

const isPageLink = (pageItem: PaginationItem): pageItem is PaginationItemWithPageLink => {
  return 'pageLink' in pageItem;
};
export function Pagination<T extends PaginationItemWithPageLink | PaginationItemWithFetchLink>({
  pageItems,
  parentHash,
  modifier,
  onClick,
}: Pagination<T>) {
  const { t } = useSonTranslation('Pagination');

  if (isNullOrUndefined(pageItems) || pageItems.length === 0) {
    return null;
  }

  const getLinkName = (pageItem: PageFetchLinkItem | PageLinkItem) => {
    return isPageLink(pageItem) ? pageItem.pageLink.name : pageItem.fetchLink.name;
  };

  const getLinkUrl = (pageItem: PageFetchLinkItem | PageLinkItem) => {
    return isPageLink(pageItem) ? pageItem.pageLink.url : pageItem.fetchLink.url;
  };

  const createHref = (item: PageFetchLinkItem | PageLinkItem) => {
    if (item.active) {
      if (parentHash) {
        return `${getLinkUrl(item)}#${parentHash}`;
      }
      return getLinkUrl(item);
    }
    return undefined;
  };

  const getNumber = (item: PageFetchLinkItem | PageLinkItem) => {
    // current page (no href supplied, disabledLink set) => <span>
    return (
      <li className="pagination__list-item" key={murmurhash(item)}>
        <ButtonRound
          tooltipPlacement={TooltipPlacement.BOTTOM}
          active={!item.active}
          disabledLink={!item.active}
          href={createHref(item)}
          onClick={() => onClick && onClick(item as OnClickArgs<T>)}
          ariaCurrent={!item.active ? 'page' : undefined}
        >
          <span className="invisible">{t('pagination.page')} </span>
          {getLinkName(item)}
        </ButtonRound>
      </li>
    );
  };

  const getArrow = (item: PageFetchLinkItem | PageLinkItem) => {
    const titleAndText = item.type === 'next' ? t('pagination.next') : t('pagination.previous');
    const svg = item.type === 'next' ? Icons.ARROW_RIGHT : Icons.ARROW_LEFT;
    const rel = item.type === 'next' ? 'next' : 'prev';

    return (
      <li className="pagination__list-item" key={murmurhash(item)}>
        <ButtonRound
          href={createHref(item)}
          icon={svg}
          text={titleAndText}
          tooltipPlacement={item.active ? TooltipPlacement.BOTTOM : undefined}
          disabledLink={!item.active}
          onClick={() => onClick && onClick(item as OnClickArgs<T>)}
          rel={rel}
        />
      </li>
    );
  };

  // dots in pagination
  // use Math.random for hash function because pageselector object always looks the same.
  function getSelector(item: PageFetchLinkSelector | PageLinkSelector) {
    return (
      <li
        className="pagination__list-item pagination__list-item--hellip"
        key={murmurhash(item) + Math.random()}
        aria-hidden="true"
      >
        <span className="pagination__hellip">&hellip;</span>
      </li>
    );
  }

  const mapTypeToComponent = (pageItem: PaginationItem) => {
    switch (pageItem.type) {
      case 'page':
        return getNumber(pageItem);
      case 'pageSelector':
        return getSelector(pageItem);
      case 'previous':
      case 'next':
        return getArrow(pageItem);
      default:
        return null;
    }
  };

  return (
    <nav
      className={bem('pagination', modifier)}
      id={GlobalDocumentIds.pagination}
      aria-labelledby="pagination-headline"
    >
      <h2 className="invisible" id="pagination-headline">
        {t('pagination.headline')}
      </h2>
      <ul className="pagination__list">{pageItems.map(mapTypeToComponent)}</ul>
    </nav>
  );
}
